import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, Timestamp, limit, startAfter, orderBy, addDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { FiShare2, FiTrash2, FiUsers, FiClock, FiBarChart2, FiBook, FiCalendar, FiChevronLeft, FiChevronRight, FiAward, FiX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import LeaderboardModal from './LeaderboardModal';

function QuizHistory() {
  const { currentUser } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [error, setError] = useState('');
  const [pageSize] = useState(10);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', user?.uid);
      if (user) {
        loadQuizzes();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadQuizzes = async (isNextPage = false) => {
    if (!auth.currentUser) {
      console.log('No authenticated user');
      setLoading(false);
      return;
    }

    try {
      const userId = auth.currentUser.uid;
      console.log('Fetching quizzes for user:', userId);
      
      const quizzesRef = collection(db, 'quizzes');
      let q = query(
        quizzesRef, 
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      if (isNextPage && lastVisible) {
        q = query(
          quizzesRef,
          where('createdBy', '==', userId),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(pageSize)
        );
      }
      
      const quizzesSnap = await getDocs(q);
      
      if (quizzesSnap.empty) {
        setHasMore(false);
        if (!isNextPage) setQuizzes([]);
        setLoading(false); // Ensure loading is set to false even if empty
        return;
      }

      setLastVisible(quizzesSnap.docs[quizzesSnap.docs.length - 1]);
      
      const newQuizData = await Promise.all(quizzesSnap.docs.map(async (doc) => {
        const data = doc.data();
        // In Quiz History, participants are from public-quiz-participants
        // associated with this quizId, not the internal quiz.results array.
        const participantsData = await loadParticipants(doc.id);
        
        // Calculate overall average score for this quiz instance across participants
         const completedParticipants = participantsData.filter(p => p.completed);
         const averageScore = completedParticipants.length > 0 
           ? Math.round(completedParticipants.reduce((sum, p) => sum + (p.score || 0), 0) / completedParticipants.length)
           : 0;

        return {
          id: doc.id,
          ...data,
          quizName: data.quizName || data.setTitle || 'Untitled Quiz',
          subject: data.subject || 'No Subject',
          topic: data.topic || 'No Topic',
          timeLimit: data.timeLimit || 15,
          questionCount: data.questions?.length || 0,
          createdAt: data.createdAt instanceof Timestamp 
            ? data.createdAt.toDate() 
            : new Date(data.createdAt?.seconds * 1000 || Date.now()), // Handle potential undefined or non-Timestamp
          participants: participantsData,
          averageScore: averageScore // Add average score to quiz object
        };
      }));

      setQuizzes(prev => isNextPage ? [...prev, ...newQuizData] : newQuizData);
      setHasMore(newQuizData.length === pageSize);

    } catch (error) {
      console.error('Error in loadQuizzes:', error);
      setError('Failed to load quizzes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async (quizId) => {
    try {
      const participantsRef = collection(db, 'public-quiz-participants');
      const q = query(
        participantsRef, 
        where('quizId', '==', quizId)
      );
      
      const participantsSnap = await getDocs(q);
      console.log(`Found ${participantsSnap.size} participants for quiz ${quizId}`);
      
      const participants = participantsSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startedAt: data.startedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
          timeTaken: data.completedAt && data.startedAt ? 
            Math.round((data.completedAt.toDate() - data.startedAt.toDate()) / 1000) : 
            null
        };
      });

      // Sort participants by score for leaderboard
      const sortedParticipants = [...participants].sort((a, b) => (b.score || 0) - (a.score || 0));

      console.log('Processed participants:', sortedParticipants);
      return sortedParticipants;
    } catch (error) {
      console.error('Error loading participants:', error);
      return [];
    }
  };

  const handleDeleteClick = (quizId) => {
    setQuizToDelete(quizId);
    setShowConfirmDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!quizToDelete) return;

    try {
      setLoading(true);
      const quizRef = doc(db, 'quizzes', quizToDelete);

      // Get all participants for this quiz
      const participantsSnap = await getDocs(query(
        collection(db, 'public-quiz-participants'),
        where('quizId', '==', quizToDelete)
      ));

      // Use a batch to delete quiz and participants
      const batch = writeBatch(db);
      batch.delete(quizRef);
      participantsSnap.docs.forEach(doc => batch.delete(doc.ref));

      await batch.commit();

      setQuizzes(quizzes.filter(quiz => quiz.id !== quizToDelete));
      console.log(`Quiz ${quizToDelete} and ${participantsSnap.size} participants deleted.`);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Error deleting quiz:', error);
      setError('Failed to delete quiz.');
    } finally {
      setLoading(false);
      setQuizToDelete(null);
      setShowConfirmDeleteModal(false);
    }
  };

  const cancelDelete = () => {
    setQuizToDelete(null);
    setShowConfirmDeleteModal(false);
  };

  const shareQuiz = (quizId) => {
    const quizUrl = `${window.location.origin}/public-quiz/${quizId}`;
    navigator.clipboard.writeText(quizUrl);
    setShowShareModal(true);
    // Auto-hide the modal after 2 seconds
    setTimeout(() => setShowShareModal(false), 2000);
  };

   const viewLeaderboard = (quiz) => {
    setSelectedQuiz(quiz);
    setShowLeaderboardModal(true);
  };

  const sortQuizzes = (quizList) => {
    switch (sortBy) {
      case 'name':
        return [...quizList].sort((a, b) => 
          (a.quizName || '').localeCompare(b.quizName || '')
        );
      case 'participants':
        // Sort by total participants, then completed participants
        return [...quizList].sort((a, b) => {
          const totalA = a.participants?.length || 0;
          const totalB = b.participants?.length || 0;
          if (totalB !== totalA) return totalB - totalA;
          const completedA = a.participants?.filter(p => p.completed).length || 0;
          const completedB = b.participants?.filter(p => p.completed).length || 0;
          return completedB - completedA;
        });
      case 'score':
        return [...quizList].sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0));
      case 'date':
      default:
        // Ensure comparison handles Date objects correctly
        return [...quizList].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }
  };

  const getFilteredAndSortedQuizzes = () => {
    const filtered = quizzes.filter(quiz => 
      (quiz.quizName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quiz.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quiz.topic || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    return sortQuizzes(filtered);
  };

  const formatTime = (seconds) => {
    if (seconds === null) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Detailed modal for quiz overview and participant list
   const QuizDetailsModal = ({ quiz, onClose }) => {
    if (!quiz) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-[rgb(130,88,18)]">{quiz.quizName} Details</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
              <FiX size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
             <div className="border rounded-lg p-4 bg-gray-50">
                 <h4 className="font-semibold mb-2">Quiz Overview</h4>
                 <p className="text-gray-600 text-sm"><span className="font-medium">Subject:</span> {quiz.subject}</p>
                 <p className="text-gray-600 text-sm"><span className="font-medium">Topic:</span> {quiz.topic}</p>
                 <p className="text-gray-600 text-sm"><span className="font-medium">Questions:</span> {quiz.questionCount}</p>
                 <p className="text-gray-600 text-sm"><span className="font-medium">Time Limit:</span> {quiz.timeLimit} minutes</p>
                 <p className="text-gray-600 text-sm"><span className="font-medium">Created On:</span> {new Date(quiz.createdAt).toLocaleDateString()}</p>
                 <p className="text-gray-600 text-sm font-bold mt-2"><span className="font-medium">Overall Average Score:</span> {quiz.averageScore}%</p>
             </div>
             
             <div className="border rounded-lg p-4 bg-gray-50">
                 <h4 className="font-semibold mb-2">Actions</h4>
                 <div className="flex flex-col space-y-3">
                     <button
                         onClick={() => shareQuiz(quiz.id)}
                         className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                     >
                         <FiShare2 size={16} className="mr-2" /> Share Quiz Link
                     </button>
                     <button
                         onClick={() => viewLeaderboard(quiz)}
                         className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                     >
                         <FiAward size={16} className="mr-2" /> View Participant Leaderboard
                     </button>
                     <button
                         onClick={() => handleDeleteClick(quiz.id)}
                         className="flex items-center justify-center px-4 py-2 border border-red-500 text-red-600 rounded-md hover:bg-red-50 text-sm"
                     >
                         <FiTrash2 size={16} className="mr-2" /> Delete Quiz
                     </button>
                 </div>
             </div>
          </div>


          <div>
            <h4 className="text-xl font-semibold mb-3">Participants ({quiz.participants?.length || 0})</h4>
            {quiz.participants?.length === 0 ? (
              <p className="text-gray-500">No participants for this quiz yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score (%)</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Taken</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quiz.participants?.map(participant => (
                      <tr key={participant.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{participant.userName || 'Anonymous'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{participant.score ?? 'N/A'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{participant.completed ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTime(participant.timeTaken)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };

  // Small helper component for stats boxes
   const StatBox = ({ icon, label, value }) => (
    <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex-shrink-0 text-[rgb(130,88,18)]">
        {icon}
      </div>
      <div className="ml-3">
        <div className="text-sm font-medium text-gray-500">{label}</div>
        <div className="text-lg font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );

  const handleLoadMore = () => {
     if (hasMore && !loading) {
       loadQuizzes(true);
     }
  };

  // Share Modal Component
  const ShareModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
        <div className="flex items-center justify-center space-x-3">
          <FiShare2 className="text-[rgb(130,88,18)]" size={24} />
          <p className="text-lg font-medium text-gray-900">Quiz link copied to clipboard!</p>
        </div>
      </div>
    </div>
  );

  // Confirmation Modal Component
  const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Confirm Action</h3>
        <p className="mb-6 text-gray-700">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 rounded-lg shadow-lg bg-white">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Your Quiz History</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row items-center justify-between mb-6 space-y-4 md:space-y-0 md:space-x-4">
        <input
          type="text"
          placeholder="Search quizzes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/3 rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)]"
        />
        <div className="flex items-center space-x-2">
          <label htmlFor="sort" className="text-sm font-medium text-gray-700">Sort by:</label>
          <select
            id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] text-sm"
          >
            <option value="date">Date Created</option>
            <option value="name">Quiz Name</option>
            <option value="participants">Participants</option>
             <option value="score">Average Score</option>
          </select>
        </div>
      </div>

      {loading && quizzes.length === 0 ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div>
        </div>
      ) : getFilteredAndSortedQuizzes().length === 0 ? (
        <p className="text-gray-500 text-center">No quizzes found matching your criteria.</p>
      ) : (
        <div className="space-y-4 md:space-y-0">
           {/* Desktop Table View */}
           <div className="hidden md:block overflow-x-auto">
             <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
               <thead className="bg-gray-100">
                 <tr>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Quiz Name</th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Subject</th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Topic</th>
                   <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Questions</th>
                   <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Participants (Completed)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Avg. Score</th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Created On</th>
                   <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                 </tr>
               </thead>
               <tbody className="bg-white divide-y divide-gray-200">
                 {getFilteredAndSortedQuizzes().map(quiz => (
                   <tr key={quiz.id} className="hover:bg-gray-50">
                     <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{quiz.quizName}</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{quiz.subject}</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{quiz.topic}</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">{quiz.questionCount}</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                        {quiz.participants?.length || 0} ({quiz.participants?.filter(p => p.completed).length || 0})
                     </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">{quiz.averageScore}%</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{new Date(quiz.createdAt).toLocaleDateString()}</td>
                     <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-right">
                       <div className="flex justify-end space-x-2">
                         <button
                           onClick={() => {
                             setSelectedQuiz(quiz);
                             setShowDetails(true);
                           }}
                           className="text-blue-600 hover:text-blue-800" title="View Details"
                         >
                           <FiBarChart2 size={18} />
                         </button>
                          <button
                           onClick={() => viewLeaderboard(quiz)}
                           className="text-green-600 hover:text-green-800" title="View Leaderboard"
                         >
                           <FiAward size={18} />
                         </button>
                         <button
                           onClick={() => shareQuiz(quiz.id)}
                           className="text-purple-600 hover:text-purple-800" title="Share Quiz"
                         >
                           <FiShare2 size={18} />
                         </button>
                         <button
                           onClick={() => handleDeleteClick(quiz.id)}
                           className="text-red-600 hover:text-red-800" title="Delete Quiz"
                         >
                           <FiTrash2 size={18} />
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>

            {/* Mobile Card View */}
           <div className="md:hidden space-y-4">
              {getFilteredAndSortedQuizzes().map(quiz => (
                 <div key={quiz.id} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                   <div className="flex justify-between items-start mb-3">
                     <div className="font-bold text-gray-800">{quiz.quizName}</div>
                      <div className="flex space-x-2">
                         <button
                           onClick={() => {
                             setSelectedQuiz(quiz);
                             setShowDetails(true);
                           }}
                           className="p-1 text-blue-600 hover:text-blue-800" title="View Details"
                         >
                           <FiBarChart2 size={18} />
                         </button>
                          <button
                           onClick={() => viewLeaderboard(quiz)}
                           className="p-1 text-green-600 hover:text-green-800" title="View Leaderboard"
                         >
                           <FiAward size={18} />
                         </button>
                         <button
                           onClick={() => shareQuiz(quiz.id)}
                           className="p-1 text-purple-600 hover:text-purple-800" title="Share Quiz"
                         >
                           <FiShare2 size={18} />
                         </button>
                         <button
                           onClick={() => handleDeleteClick(quiz.id)}
                           className="p-1 text-red-600 hover:text-red-800" title="Delete Quiz"
                         >
                           <FiTrash2 size={18} />
                         </button>
                      </div>
                   </div>
                   <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                     <div><span className="font-medium">Subject:</span> {quiz.subject}</div>
                     <div><span className="font-medium">Topic:</span> {quiz.topic}</div>
                     <div><span className="font-medium">Questions:</span> {quiz.questionCount}</div>
                     <div><span className="font-medium">Participants:</span> {quiz.participants?.length || 0} ({quiz.participants?.filter(p => p.completed).length || 0})</div>
                      <div><span className="font-medium">Avg. Score:</span> {quiz.averageScore}%</div>
                     <div><span className="font-medium">Created On:</span> {new Date(quiz.createdAt).toLocaleDateString()}</div>
                   </div>
                 </div>
              ))}
           </div>
        </div>
      )}

      {hasMore && getFilteredAndSortedQuizzes().length > 0 && (
        <div className="text-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="bg-[rgb(130,88,18)] text-white px-6 py-2 rounded-md hover:bg-[rgb(110,68,0)] disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && <ShareModal />}

      {/* Confirmation Modal */}
      {showConfirmDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this quiz and all associated participant data?"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {/* Quiz Details Modal */}
      {showDetails && selectedQuiz && (
        <QuizDetailsModal quiz={selectedQuiz} onClose={() => setShowDetails(false)} />
      )}

      {/* Leaderboard Modal */}
       {showLeaderboardModal && selectedQuiz && (
         <LeaderboardModal
           quizId={selectedQuiz.id} // Pass the quiz ID
           quizName={selectedQuiz.quizName} // Pass the quiz name
           participants={selectedQuiz.participants} // Pass the loaded participants
           onClose={() => setShowLeaderboardModal(false)}
         />
       )}
    </div>
  );
}

export default QuizHistory; 